import { useEffect, useId, type RefObject } from 'react';
import { create } from 'zustand';

/**
 * Which open forms hold typing that has not been saved.
 *
 * A form registers while it is on screen and has been typed in. Leaving the
 * screen, signing out, or closing the window then asks first, instead of
 * throwing away a half-entered shipment without a word.
 */
interface UnsavedState {
  forms: Set<string>;
  mark: (id: string, dirty: boolean) => void;
}

export const useUnsaved = create<UnsavedState>((set) => ({
  forms: new Set(),
  mark: (id, dirty) =>
    set((s) => {
      if (s.forms.has(id) === dirty) return s;
      const forms = new Set(s.forms);
      if (dirty) forms.add(id);
      else forms.delete(id);
      return { forms };
    }),
}));

export function hasUnsavedWork(): boolean {
  return useUnsaved.getState().forms.size > 0;
}

/** Forgets every mark — after the person chose to leave without saving. */
export function discardUnsavedWork(): void {
  useUnsaved.setState({ forms: new Set() });
}

/**
 * Marks the form inside `ref` as having unsaved work once someone types or
 * picks something in it, and clears the mark when the form closes (saved or
 * cancelled — either way the typing is no longer at risk).
 *
 * Listens for input and change events rather than asking each form for its
 * state, so every form that uses FormPanel or ComposePanel is covered without
 * any change to the form itself.
 */
export function useTrackUnsaved(ref: RefObject<HTMLElement | null>): string {
  const id = useId();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onEdit = (e: Event) => {
      // A search box inside a form filters a list; it is not the form's data.
      const target = e.target as HTMLElement | null;
      if (target?.closest('[data-unsaved-ignore]')) return;
      useUnsaved.getState().mark(id, true);
    };
    el.addEventListener('input', onEdit);
    el.addEventListener('change', onEdit);
    return () => {
      el.removeEventListener('input', onEdit);
      el.removeEventListener('change', onEdit);
      useUnsaved.getState().mark(id, false);
    };
  }, [id, ref]);

  return id;
}

/** Whether the form `id` (from useTrackUnsaved) has unsaved work. */
export function useIsUnsaved(id: string): boolean {
  return useUnsaved((s) => s.forms.has(id));
}

/**
 * The browser's own "Leave site?" prompt on reload or window close, while any
 * form holds unsaved work. Browsers show their fixed wording; ours cannot be
 * used there.
 */
export function useUnsavedCloseWarning(): void {
  useEffect(() => {
    function onBeforeUnload(e: BeforeUnloadEvent) {
      if (!hasUnsavedWork()) return;
      e.preventDefault();
      // Older browsers need returnValue set to show the prompt.
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);
}

/**
 * Marks unsaved work from a flag the screen keeps itself — for screens whose
 * changes are not typed into inputs, such as ticking cells in the permission
 * grid, which `useTrackUnsaved` cannot see. Cleared when the screen closes.
 */
export function useUnsavedFlag(dirty: boolean): void {
  const id = useId();
  useEffect(() => {
    useUnsaved.getState().mark(id, dirty);
  }, [id, dirty]);
  useEffect(() => () => useUnsaved.getState().mark(id, false), [id]);
}
